import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Lock, LogOut, Calendar as CalIcon, ClipboardList, Star, Users,
  CheckCircle2, Trash2, Eye, EyeOff, Ban, Sparkles, Mail, MailX,
} from "lucide-react";
import { api, adminApi, ADMIN_TOKEN_KEY } from "@/lib/api";
import { Calendar } from "@/components/ui/calendar";

const TABS = [
  { id: "bookings", label: "Bookings", icon: ClipboardList },
  { id: "meet-greets", label: "Meet & Greets", icon: Users },
  { id: "reviews", label: "Reviews", icon: Star },
  { id: "availability", label: "Availability", icon: CalIcon },
];

export default function OwnerPage() {
  const [authed, setAuthed] = useState(!!localStorage.getItem(ADMIN_TOKEN_KEY));
  const [tab, setTab] = useState("bookings");
  const [config, setConfig] = useState({ email_enabled: false });

  useEffect(() => {
    if (authed) api.get("/config").then((r) => setConfig(r.data)).catch(() => {});
  }, [authed]);

  if (!authed) return <Login onSuccess={() => setAuthed(true)} />;

  return (
    <div data-testid="owner-page" className="py-12 sm:py-16">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <header className="flex flex-col sm:flex-row sm:items-end justify-between gap-5 mb-6">
          <div>
            <span className="font-script text-2xl text-[#b85b7e]">owner dashboard</span>
            <h1 className="font-display font-bold text-4xl sm:text-5xl text-[#3F2A56] leading-tight mt-1">
              Aurora&rsquo;s desk
            </h1>
            <p className="text-[#5C4A73] mt-1">Manage bookings, reviews, meet &amp; greets, and your calendar.</p>
          </div>
          <button
            data-testid="owner-logout"
            onClick={() => {
              localStorage.removeItem(ADMIN_TOKEN_KEY);
              setAuthed(false);
            }}
            className="self-start sm:self-auto rounded-full bg-[#3F2A56]/10 hover:bg-[#3F2A56]/15 text-[#3F2A56] font-semibold px-5 py-2.5 flex items-center gap-2"
          >
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </header>

        <EmailStatusBanner enabled={config.email_enabled} />

        <nav data-testid="owner-tabs" className="flex flex-wrap gap-2 mb-7 p-1.5 rounded-full bg-[#FFFBF0] border-2 border-[#3F2A56]/15 w-fit">
          {TABS.map((t) => (
            <button
              key={t.id}
              data-testid={`tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-4 sm:px-5 py-2.5 rounded-full text-sm font-semibold transition-all ${
                tab === t.id
                  ? "bg-[#3F2A56] text-[#F4E8C8]"
                  : "text-[#3F2A56] hover:bg-[#3F2A56]/5"
              }`}
            >
              <t.icon className="w-4 h-4" />
              {t.label}
            </button>
          ))}
        </nav>

        <div className="parchment-card rounded-3xl p-5 sm:p-8">
          {tab === "bookings" && <BookingsTab />}
          {tab === "meet-greets" && <MeetGreetsTab />}
          {tab === "reviews" && <ReviewsTab />}
          {tab === "availability" && <AvailabilityTab />}
        </div>
      </div>
    </div>
  );
}

function Login({ onSuccess }) {
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/admin/login", { password });
      localStorage.setItem(ADMIN_TOKEN_KEY, data.token);
      toast.success("Welcome back, Aurora!");
      onSuccess();
    } catch {
      toast.error("Incorrect password.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="owner-login" className="py-20">
      <div className="max-w-md mx-auto px-6 sm:px-8">
        <div className="parchment-card rounded-3xl p-8 text-center">
          <div className="w-14 h-14 rounded-full bg-[#3F2A56] text-[#F4E8C8] grid place-items-center mx-auto">
            <Lock className="w-6 h-6" />
          </div>
          <h1 className="font-display font-bold text-3xl text-[#3F2A56] mt-4">
            Owner sign-in
          </h1>
          <p className="text-[#5C4A73] mt-1">Welcome back. Please enter your password.</p>
          <form onSubmit={submit} className="mt-7 space-y-4 text-left">
            <label className="block">
              <span className="block text-sm font-semibold text-[#2A1C3A] mb-1.5">
                Password
              </span>
              <input
                data-testid="owner-password-input"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-2xl bg-[#FFFBF0] border-2 border-[#3F2A56]/20 px-4 py-3 text-[#2A1C3A] focus:outline-none focus:ring-2 focus:ring-[#4A6741] focus:border-[#4A6741]"
              />
            </label>
            <button
              type="submit"
              data-testid="owner-login-submit"
              disabled={submitting}
              className="w-full rounded-full bg-[#3F2A56] hover:bg-[#523870] text-[#F4E8C8] font-semibold px-6 py-3.5 transition-all hover:-translate-y-0.5 disabled:opacity-60"
            >
              {submitting ? "Signing in…" : "Sign in"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

const STATUS_COLORS = {
  pending: "bg-[#D4AF37]/20 text-[#7a6520] border-[#D4AF37]/40",
  confirmed: "bg-[#4A6741]/15 text-[#2c4327] border-[#4A6741]/40",
  paid: "bg-[#4A6741]/25 text-[#1f3a1a] border-[#4A6741]/50",
  completed: "bg-[#3F2A56]/15 text-[#3F2A56] border-[#3F2A56]/30",
  cancelled: "bg-[#b85b7e]/15 text-[#7a2c4a] border-[#b85b7e]/40",
};

function StatusPill({ status }) {
  return (
    <span
      data-testid={`status-${status}`}
      className={`inline-block px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider border ${
        STATUS_COLORS[status] || ""
      }`}
    >
      {status}
    </span>
  );
}

function BookingsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () =>
    adminApi()
      .get("/admin/bookings")
      .then((r) => setItems(r.data))
      .catch(() => toast.error("Couldn't load bookings"))
      .finally(() => setLoading(false));

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const updateStatus = async (id, status) => {
    try {
      await adminApi().patch(`/admin/bookings/${id}/status`, { status });
      toast.success(`Status → ${status}`);
      load();
    } catch {
      toast.error("Update failed");
    }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this booking permanently?")) return;
    await adminApi().delete(`/admin/bookings/${id}`);
    toast.success("Booking deleted");
    load();
  };

  if (loading) return <Empty>Loading bookings…</Empty>;
  if (items.length === 0) return <Empty>No bookings yet. They&rsquo;ll appear here.</Empty>;

  return (
    <div data-testid="bookings-list" className="space-y-4">
      {items.map((b) => (
        <article
          key={b.id}
          data-testid={`booking-${b.id}`}
          className="rounded-2xl border-2 border-[#3F2A56]/15 bg-[#FFFBF0] p-5"
        >
          <header className="flex flex-wrap items-start justify-between gap-3 mb-3">
            <div>
              <h3 className="font-display text-xl font-semibold text-[#3F2A56]">
                {b.client_name} <span className="font-script text-[#b85b7e] text-lg">&amp; {b.pet_name}</span>
              </h3>
              <p className="text-sm text-[#5C4A73]">
                {b.email} · {b.phone}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <StatusPill status={b.status} />
              <span className="font-display text-2xl font-bold text-[#3F2A56]">
                ${b.estimated_total.toFixed(2)}
              </span>
            </div>
          </header>
          <dl className="grid sm:grid-cols-3 gap-3 text-sm">
            <Detail label="Service" value={b.service} />
            <Detail label="Start" value={b.start_date} />
            {b.end_date && <Detail label="End" value={b.end_date} />}
            {b.addons?.length > 0 && <Detail label="Add-ons" value={b.addons.join(", ")} />}
            {b.message && <Detail label="Notes" value={b.message} />}
            <Detail label="Submitted" value={new Date(b.created_at).toLocaleString()} />
          </dl>
          <footer className="mt-4 flex flex-wrap gap-2">
            {["confirmed", "paid", "completed", "cancelled"].map((s) => (
              <button
                key={s}
                data-testid={`booking-${b.id}-set-${s}`}
                onClick={() => updateStatus(b.id, s)}
                disabled={b.status === s}
                className="rounded-full text-xs font-semibold px-3.5 py-1.5 bg-[#3F2A56]/10 hover:bg-[#3F2A56] hover:text-[#F4E8C8] text-[#3F2A56] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Mark {s}
              </button>
            ))}
            <button
              data-testid={`booking-${b.id}-delete`}
              onClick={() => remove(b.id)}
              className="ml-auto rounded-full text-xs font-semibold px-3.5 py-1.5 bg-[#b85b7e]/10 hover:bg-[#b85b7e] hover:text-white text-[#7a2c4a] flex items-center gap-1.5 transition-colors"
            >
              <Trash2 className="w-3.5 h-3.5" /> Delete
            </button>
          </footer>
        </article>
      ))}
    </div>
  );
}

function MeetGreetsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () =>
    adminApi()
      .get("/admin/meet-greets")
      .then((r) => setItems(r.data))
      .catch(() => toast.error("Couldn't load meet & greets"))
      .finally(() => setLoading(false));

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const updateStatus = async (id, status) => {
    try {
      await adminApi().patch(`/admin/meet-greets/${id}/status`, { status });
      toast.success(`Status → ${status}`);
      load();
    } catch {
      toast.error("Update failed");
    }
  };

  if (loading) return <Empty>Loading…</Empty>;
  if (items.length === 0) return <Empty>No meet & greets yet.</Empty>;

  return (
    <div data-testid="mg-list" className="space-y-4">
      {items.map((m) => (
        <article
          key={m.id}
          data-testid={`mg-${m.id}`}
          className="rounded-2xl border-2 border-[#3F2A56]/15 bg-[#FFFBF0] p-5"
        >
          <header className="flex flex-wrap items-start justify-between gap-3 mb-3">
            <div>
              <h3 className="font-display text-xl font-semibold text-[#3F2A56]">
                {m.client_name} <span className="font-script text-[#b85b7e] text-lg">&amp; {m.pet_name}</span>
              </h3>
              <p className="text-sm text-[#5C4A73]">{m.email} · {m.phone}</p>
            </div>
            <div className="flex items-center gap-3">
              <StatusPill status={m.status} />
              <div className="text-right">
                <div className="font-display font-semibold text-[#3F2A56]">{m.date}</div>
                <div className="text-sm text-[#5C4A73]">{m.time_slot}</div>
              </div>
            </div>
          </header>
          <dl className="grid sm:grid-cols-3 gap-3 text-sm">
            <Detail label="Pet type" value={m.pet_type} />
            {m.address && <Detail label="Address" value={m.address} />}
            {m.notes && <Detail label="Notes" value={m.notes} />}
          </dl>
          <footer className="mt-4 flex flex-wrap gap-2">
            {["confirmed", "completed", "cancelled"].map((s) => (
              <button
                key={s}
                data-testid={`mg-${m.id}-set-${s}`}
                onClick={() => updateStatus(m.id, s)}
                disabled={m.status === s}
                className="rounded-full text-xs font-semibold px-3.5 py-1.5 bg-[#3F2A56]/10 hover:bg-[#3F2A56] hover:text-[#F4E8C8] text-[#3F2A56] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Mark {s}
              </button>
            ))}
          </footer>
        </article>
      ))}
    </div>
  );
}

function ReviewsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () =>
    adminApi()
      .get("/admin/reviews")
      .then((r) => setItems(r.data))
      .catch(() => toast.error("Couldn't load reviews"))
      .finally(() => setLoading(false));

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = async (id) => {
    await adminApi().patch(`/admin/reviews/${id}/visibility`);
    toast.success("Visibility updated");
    load();
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this review permanently?")) return;
    await adminApi().delete(`/admin/reviews/${id}`);
    toast.success("Review deleted");
    load();
  };

  if (loading) return <Empty>Loading…</Empty>;
  if (items.length === 0) return <Empty>No reviews yet.</Empty>;

  return (
    <div data-testid="reviews-admin-list" className="space-y-4">
      {items.map((r) => (
        <article
          key={r.id}
          data-testid={`admin-review-${r.id}`}
          className={`rounded-2xl border-2 p-5 ${
            r.published
              ? "border-[#3F2A56]/15 bg-[#FFFBF0]"
              : "border-[#b85b7e]/30 bg-[#b85b7e]/5"
          }`}
        >
          <header className="flex flex-wrap items-start justify-between gap-3 mb-2">
            <div>
              <div className="flex items-center gap-2">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star
                    key={i}
                    className={`w-4 h-4 ${
                      i < r.rating ? "fill-[#D4AF37] text-[#D4AF37]" : "text-[#3F2A56]/20"
                    }`}
                  />
                ))}
                {!r.published && (
                  <span className="text-xs font-bold uppercase tracking-wider px-2.5 py-0.5 rounded-full bg-[#b85b7e]/15 text-[#7a2c4a]">
                    Hidden
                  </span>
                )}
              </div>
              <h3 className="font-display text-xl font-semibold text-[#3F2A56] mt-1">
                {r.title}
              </h3>
              <p className="text-sm text-[#5C4A73]">
                {r.client_name}{r.pet_name ? ` & ${r.pet_name}` : ""}
              </p>
            </div>
            <span className="text-xs text-[#5C4A73]">
              {new Date(r.created_at).toLocaleDateString()}
            </span>
          </header>
          <p className="text-[#2A1C3A] mt-2 leading-relaxed">{r.body}</p>
          <footer className="mt-4 flex gap-2">
            <button
              data-testid={`admin-review-${r.id}-toggle`}
              onClick={() => toggle(r.id)}
              className="rounded-full text-xs font-semibold px-3.5 py-1.5 bg-[#3F2A56]/10 hover:bg-[#3F2A56] hover:text-[#F4E8C8] text-[#3F2A56] flex items-center gap-1.5 transition-colors"
            >
              {r.published ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
              {r.published ? "Hide" : "Show"}
            </button>
            <button
              data-testid={`admin-review-${r.id}-delete`}
              onClick={() => remove(r.id)}
              className="rounded-full text-xs font-semibold px-3.5 py-1.5 bg-[#b85b7e]/10 hover:bg-[#b85b7e] hover:text-white text-[#7a2c4a] flex items-center gap-1.5 transition-colors"
            >
              <Trash2 className="w-3.5 h-3.5" /> Delete
            </button>
          </footer>
        </article>
      ))}
    </div>
  );
}

function fmtISO(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function AvailabilityTab() {
  const [blocked, setBlocked] = useState([]);
  const [reason, setReason] = useState("");
  const [pending, setPending] = useState(null);

  const load = () =>
    api.get("/availability").then((r) => setBlocked(r.data || []));
  useEffect(() => {
    load();
  }, []);

  const blockedDates = blocked.map((b) => new Date(b.date + "T00:00:00"));
  const isBlocked = (iso) => blocked.some((b) => b.date === iso);

  const handlePick = (d) => {
    if (!d) return;
    const iso = fmtISO(d);
    if (isBlocked(iso)) {
      // unblock
      adminApi()
        .delete(`/admin/availability/${iso}`)
        .then(() => {
          toast.success(`${iso} reopened`);
          load();
        })
        .catch(() => toast.error("Couldn't unblock"));
    } else {
      setPending(iso);
    }
  };

  const confirmBlock = async () => {
    if (!pending) return;
    try {
      await adminApi().post("/admin/availability/block", { date: pending, reason });
      toast.success(`${pending} blocked`);
      setPending(null);
      setReason("");
      load();
    } catch {
      toast.error("Couldn't block date");
    }
  };

  return (
    <div data-testid="availability-tab" className="grid lg:grid-cols-[1fr_1fr] gap-7">
      <div>
        <h3 className="font-display text-2xl font-semibold text-[#3F2A56] mb-1">
          Block / unblock dates
        </h3>
        <p className="text-sm text-[#5C4A73] mb-4 flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-[#4A6741]" />
          Click a date to block; click a blocked date to reopen it.
        </p>
        <div className="inline-block rounded-2xl bg-[#FFFBF0] border-2 border-[#3F2A56]/15 p-2">
          <Calendar
            data-testid="availability-calendar"
            mode="single"
            onSelect={handlePick}
            modifiers={{ blocked: blockedDates }}
            modifiersClassNames={{
              blocked: "bg-[#b85b7e] text-white hover:bg-[#a04a6c]",
            }}
            disabled={(d) => {
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              return d < today;
            }}
          />
        </div>

        {pending && (
          <div
            data-testid="block-confirm"
            className="mt-5 rounded-2xl p-5 bg-[#3F2A56] text-[#F4E8C8]"
          >
            <p className="font-semibold">Block {pending}?</p>
            <input
              data-testid="block-reason-input"
              placeholder="Reason (optional, e.g. vacation)"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full mt-3 rounded-xl bg-[#F4E8C8] text-[#2A1C3A] px-3 py-2 focus:outline-none"
            />
            <div className="mt-3 flex gap-2">
              <button
                data-testid="block-confirm-btn"
                onClick={confirmBlock}
                className="rounded-full bg-[#F4E8C8] text-[#3F2A56] font-semibold px-4 py-2 text-sm hover:-translate-y-0.5 transition-transform flex items-center gap-1.5"
              >
                <Ban className="w-4 h-4" /> Block date
              </button>
              <button
                onClick={() => {
                  setPending(null);
                  setReason("");
                }}
                className="rounded-full bg-[#F4E8C8]/15 hover:bg-[#F4E8C8]/25 font-semibold px-4 py-2 text-sm"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      <div>
        <h3 className="font-display text-2xl font-semibold text-[#3F2A56] mb-3">
          Currently blocked ({blocked.length})
        </h3>
        {blocked.length === 0 ? (
          <Empty>No dates blocked — calendar is wide open.</Empty>
        ) : (
          <ul data-testid="blocked-list" className="space-y-2">
            {blocked.map((b) => (
              <li
                key={b.id}
                data-testid={`blocked-${b.date}`}
                className="flex items-center justify-between gap-3 rounded-2xl bg-[#FFFBF0] border-2 border-[#3F2A56]/15 px-4 py-3"
              >
                <div>
                  <div className="font-display font-semibold text-[#3F2A56]">
                    {new Date(b.date + "T00:00:00").toLocaleDateString("en-US", {
                      weekday: "long",
                      month: "long",
                      day: "numeric",
                      year: "numeric",
                    })}
                  </div>
                  {b.reason && <div className="text-sm text-[#5C4A73]">{b.reason}</div>}
                </div>
                <button
                  data-testid={`unblock-${b.date}`}
                  onClick={async () => {
                    await adminApi().delete(`/admin/availability/${b.date}`);
                    toast.success("Date reopened");
                    load();
                  }}
                  className="rounded-full bg-[#4A6741]/15 hover:bg-[#4A6741] hover:text-white text-[#2c4327] font-semibold px-4 py-2 text-sm flex items-center gap-1.5 transition-colors"
                >
                  <CheckCircle2 className="w-4 h-4" /> Reopen
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function Detail({ label, value }) {
  return (
    <div>
      <dt className="text-[10px] uppercase tracking-widest text-[#5C4A73]">{label}</dt>
      <dd className="font-display text-base font-semibold text-[#3F2A56] leading-tight">{value}</dd>
    </div>
  );
}

function Empty({ children }) {
  return (
    <div className="p-10 text-center text-[#5C4A73]">{children}</div>
  );
}

function EmailStatusBanner({ enabled }) {
  if (enabled) {
    return (
      <div
        data-testid="email-status-on"
        className="mb-7 flex items-start gap-3 rounded-2xl border-2 border-[#4A6741]/30 bg-[#E8F0E6] p-4"
      >
        <Mail className="w-5 h-5 text-[#4A6741] shrink-0 mt-0.5" />
        <div className="text-sm text-[#2A1C3A]">
          <strong className="text-[#2c4327]">Email notifications are on.</strong>{" "}
          You&rsquo;ll get an email for every new booking, meet &amp; greet, and review. Clients get confirmations too.
        </div>
      </div>
    );
  }
  return (
    <div
      data-testid="email-status-off"
      className="mb-7 flex items-start gap-3 rounded-2xl border-2 border-dashed border-[#b85b7e]/40 bg-[#FFFBF0] p-4"
    >
      <MailX className="w-5 h-5 text-[#b85b7e] shrink-0 mt-0.5" />
      <div className="text-sm text-[#5C4A73]">
        <strong className="text-[#3F2A56]">Email notifications are off.</strong>{" "}
        Set <code className="px-1.5 py-0.5 rounded bg-[#3F2A56]/10 text-[#3F2A56]">SENDGRID_API_KEY</code> and verify{" "}
        <code className="px-1.5 py-0.5 rounded bg-[#3F2A56]/10 text-[#3F2A56]">FROM_EMAIL</code> in SendGrid&rsquo;s
        Single Sender Verification to start receiving notifications.
      </div>
    </div>
  );
}
